import React, { useState, useEffect, useCallback, useRef } from 'react';

import { capitalize } from 'lodash';

import { Button, Alert } from '@shared/components/bootstrap';

import { DiscountSelector } from '@admin/components/discount_selector/discount_selector';
import { copyToClipboard } from '@admin/components/helpers/clipboard_utils';
import { CopyURL } from '@admin/components/helpers/copy_url';
import { Map as AdminMap } from '@admin/components/helpers/map';
import { Widget as NotesWidget } from '@admin/components/notes/widget';
import { RequiredInputHelper } from '@admin/components/required_input_helper';
import { ValidatableInput } from '@admin/components/validatable_input';
import { client } from '@admin/libraries/apollo';
import {
  useSaveLeadMutation,
  useSaveLeadWithPhoneMutation,
  useCheckoutUrlGenerateMutation,
  NoteNotableEnum,
  Coupon__Service,
  LeadFragment,
  Admin__Lead__ServiceNeeds,
} from '@admin/schema';

import { HowDidYouHearAboutUs } from './how_did_you_hear_about_us';
import { ZipCodeStatus, ServiceType } from './zip_code_status';

interface IDiscount {
  name: string;
  coupon_id?: string;
}

interface Coupon {
  id: string;
  name: string;
  eligibleServices: Coupon__Service[];
  percent: number;
  amount: number;
}
interface LeadFormProps {
  lead: LeadFragment;
  pins?: Array<{
    latitude?: number | string | null;
    longitude?: number | string | null;
    label?: google.maps.MarkerLabel;
  }>;
  coupons?: Coupon[];
}

interface ServiceAreaClassifier {
  valid: boolean;
  moving_eligible: boolean;
  full_service_eligible: boolean;
  self_storage_eligible?: boolean;
}

interface Coordinates {
  latitude?: number | string | null;
  longitude?: number | string | null;
}

// Input formatting utilities
const formatTitleCase = (text: string): string => {
  return text.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
};

const formatZip = (text: string): string => {
  // Remove non-digits and limit to 5 characters
  return text.replace(/\D/g, '').substring(0, 5);
};

export const LeadEditForm: React.FC<LeadFormProps> = ({ lead, pins, coupons }) => {
  const [coords, setCoords] = useState<Coordinates | null>(null);
  const [discount, setDiscount] = useState<IDiscount | null>(null);
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [isGeocodingInProgress, setIsGeocodingInProgress] = useState(false);
  const [lastGeocodedZip, setLastGeocodedZip] = useState<string | null>(null);
  const [zipGeocodingTimeout, setZipGeocodingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [serviceAreaClassifier, setServiceAreaClassifier] = useState<ServiceAreaClassifier | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [checkoutURL, setCheckoutURL] = useState<string | null>(null);
  const [checkoutURLError, setCheckoutURLError] = useState<string | null>(null);
  const [checkoutURLCopyStatus, setCheckoutURLCopyStatus] = useState<'default' | 'copied' | 'failed'>('default');
  const checkoutURLTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [saveLeadMutation, { loading: updateLoadingLead }] = useSaveLeadMutation({
    client,
    onCompleted: (data) => {
      const leadError = data.leadResult?.error;
      if (leadError) {
        setSaveError(leadError);
        return;
      }
      if (data.leadResult?.lead) {
        window.location.href = `/leads/${data.leadResult.lead.id}/edit`;
      }
    },
  });

  const [saveLeadWithPhoneMutation, { loading: updateLoadingPhone }] = useSaveLeadWithPhoneMutation({
    client,
    onCompleted: (data) => {
      // Check for errors from either mutation
      const leadError = data.leadResult?.error;
      const phoneError = data.phoneResult?.error;

      if (leadError || phoneError) {
        setSaveError(leadError || phoneError || 'Save failed');
        return;
      }

      if (data.leadResult?.lead) {
        window.location.href = `/leads/${data.leadResult.lead.id}/edit`;
      }
    },
  });

  const [generateCheckoutUrlMutation, { loading: checkoutURLLoading }] = useCheckoutUrlGenerateMutation({
    client,
    onCompleted: async (data) => {
      setCheckoutURLError(null);
      const result = data.checkoutUrlGenerate;

      if (result?.error) {
        setCheckoutURLError(result.error);
        return;
      }

      if (result?.checkoutUrl) {
        setCheckoutURL(result.checkoutUrl);
        // Automatically copy to clipboard
        const success = await copyToClipboard(result.checkoutUrl);
        setCheckoutURLCopyStatus(success ? 'copied' : 'failed');

        // Clear any existing timeout before creating a new one
        if (checkoutURLTimeoutRef.current) {
          clearTimeout(checkoutURLTimeoutRef.current);
        }

        // Reset status after 2 seconds
        checkoutURLTimeoutRef.current = setTimeout(() => {
          setCheckoutURLCopyStatus('default');
        }, 2000);
      }
    },
    onError: (error) => {
      setCheckoutURLError(error.message);
    },
  });

  const updateLoading = updateLoadingLead || updateLoadingPhone;

  // Channel partner logic
  const isChannelPartnerLead = (lead: any) => {
    return !!lead && !!lead.channel && lead.channel !== 'clutter';
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (zipGeocodingTimeout) {
        clearTimeout(zipGeocodingTimeout);
      }
      if (checkoutURLTimeoutRef.current) {
        clearTimeout(checkoutURLTimeoutRef.current);
      }
    };
  }, [zipGeocodingTimeout]);

  // Reset form initialization when lead changes
  useEffect(() => {
    setIsFormInitialized(false);
  }, [lead.id]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    zip: '',
    isBusiness: false,
    reportedSource: '',
    callConsent: false,
    transactionalCallConsent: false,
    smsConsent: false,
    transactionalSmsConsent: false,
  });

  // Address geocoding and service area classification
  const checkZip = useCallback(
    async (zip: string) => {
      // Prevent duplicate geocoding calls for the same zip
      if (isGeocodingInProgress || lastGeocodedZip === zip) {
        return;
      }

      setIsGeocodingInProgress(true);

      try {
        // Run both geocoding and postal code classification in parallel
        const [geocodeResponse, classifyResponse] = await Promise.all([
          fetch(`/geocodes.json?zip=${encodeURIComponent(zip)}`),
          fetch(`/postal_codes/classify.json?label=${encodeURIComponent(zip)}`),
        ]);

        let hasSuccess = false;

        // Handle geocoding response (for map coordinates)
        if (geocodeResponse.ok) {
          const geocodes = await geocodeResponse.json();
          if (geocodes.length > 0) {
            const geocode = geocodes[0];
            const geocoded = {
              latitude: Number(geocode.latitude),
              longitude: Number(geocode.longitude),
            };
            setCoords(geocoded);
            hasSuccess = true;
          }
        }

        // Handle postal code classification (for service area status)
        if (classifyResponse.ok) {
          const classifier = await classifyResponse.json();
          setServiceAreaClassifier(classifier);
          hasSuccess = true;
        }

        // Only set lastGeocodedZip if at least one call succeeded
        if (hasSuccess) {
          setLastGeocodedZip(zip);
        } else {
          // Clear cache so the same ZIP can be retried
          setLastGeocodedZip(null);
        }
      } catch (error) {
        console.error('Zip processing error:', error);
        // Clear cache on error so the same ZIP can be retried
        setLastGeocodedZip(null);
      } finally {
        setIsGeocodingInProgress(false);
      }
    },
    [isGeocodingInProgress, lastGeocodedZip],
  );

  // Update form data when lead loads (only once to avoid overriding user input)
  useEffect(() => {
    if (lead && !isFormInitialized) {
      setFormData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone?.number || '',
        zip: lead.zip || '',
        isBusiness: lead.isBusiness || false,
        reportedSource: lead.reportedSource || '',
        callConsent: lead.phone?.commercialCall || false,
        transactionalCallConsent: lead.phone?.transactionalCall || false,
        smsConsent: lead.phone?.commercialSMS || false,
        transactionalSmsConsent: lead.phone?.transactionalSMS || false,
      });

      // Only checkZip if it's different from what we've already geocoded
      if (lead.zip && lead.zip !== lastGeocodedZip) {
        checkZip(lead.zip);
      }

      setIsFormInitialized(true);
    }
  }, [lead, checkZip, lastGeocodedZip, isFormInitialized]);

  // Handle form field changes
  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle name change with title-case formatting
  const handleNameChange = (value: string) => {
    const formattedValue = formatTitleCase(value);
    handleFieldChange('name', formattedValue);
  };

  // Handle zip change with formatting and debouncing
  const handleZipChange = (value: string) => {
    const formattedValue = formatZip(value);
    handleFieldChange('zip', formattedValue);

    // Only trigger geocoding if we have a complete 5-digit zip and it's different
    if (formattedValue.length === 5 && formattedValue !== lastGeocodedZip) {
      // Clear any existing timeout to debounce the geocoding call
      if (zipGeocodingTimeout) {
        clearTimeout(zipGeocodingTimeout);
      }

      const newTimeout = setTimeout(() => {
        checkZip(formattedValue);
      }, 300); // 300ms debounce

      setZipGeocodingTimeout(newTimeout);
    }
  };

  // Handle consent toggles
  const handleCallConsentToggle = () => {
    const updatedCommercialCall = !formData.callConsent;
    setFormData((prev) => ({
      ...prev,
      callConsent: updatedCommercialCall,
      transactionalCallConsent: updatedCommercialCall,
    }));
  };

  const handleSMSConsentToggle = () => {
    const updatedCommercialSMS = !formData.smsConsent;
    setFormData((prev) => ({
      ...prev,
      smsConsent: updatedCommercialSMS,
      transactionalSmsConsent: updatedCommercialSMS,
    }));
  };

  // Handle discount selection
  const handleDiscountSelected = (selectedDiscount: IDiscount | null) => {
    if (!selectedDiscount) {
      setDiscount(null);
      setCoupon(null);
      return;
    }

    const couponID = selectedDiscount.coupon_id;
    setDiscount(selectedDiscount);
    const selectedCoupon = coupons?.find((c) => c.id === couponID);
    setCoupon(selectedCoupon || null);
  };

  // Handle source selection
  const handleSourceSelect = (source: string) => {
    handleFieldChange('reportedSource', source);
  };

  // Save lead function
  const saveLead = async () => {
    setSaveError(null);

    const hasPhone = !!lead?.phone?.id;
    const leadInput = {
      name: formData.name,
      email: formData.email,
      phoneNumber: formData.phone,
      zip: formData.zip,
      isBusiness: formData.isBusiness,
      reportedSource: formData.reportedSource,
    };

    if (hasPhone) {
      await saveLeadWithPhoneMutation({
        variables: {
          leadId: lead.id,
          leadInput,
          phoneID: lead.phone!.id,
          phoneInput: {
            commercialCall: formData.callConsent,
            transactionalCall: formData.transactionalCallConsent,
            commercialSMS: formData.smsConsent,
            transactionalSMS: formData.transactionalSmsConsent,
          },
        },
      });
    } else {
      await saveLeadMutation({
        variables: {
          leadId: lead.id,
          leadInput,
        },
      });
    }
  };

  const generateCheckoutURL = async () => {
    setCheckoutURLError(null);
    setCheckoutURL(null);
    setCheckoutURLCopyStatus('default');

    await generateCheckoutUrlMutation({
      variables: {
        leadID: lead.id,
      },
    });
  };

  return (
    <div className="clearfix" id="lead-form-container">
      <div className="flex-end-flex-container form-inline mar-btm">
        <span>
          Lead Status: <strong>{capitalize(lead?.status || '')}</strong>
        </span>
      </div>

      <div className="row mar-btm">
        <div className="col-xs-4">
          <div className="form-group reduce-margin">
            <input
              name="lead[name]"
              type="text"
              className="form-control"
              placeholder="Name"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
            />
            <RequiredInputHelper isFilled={!!formData.name} />
          </div>
        </div>

        <div className="col-xs-4">
          <div className="form-group reduce-margin">
            <ValidatableInput
              type="email"
              value={formData.email}
              skipValidationWhenBlank={true}
              name="lead[email]"
              placeholder="Email"
              onChange={(value) => handleFieldChange('email', value)}
            />
            <RequiredInputHelper isFilled={!!formData.email} />
          </div>
        </div>

        <div className="col-xs-4">
          <div className="phone-column">
            <ValidatableInput
              type="tel"
              value={formData.phone}
              skipValidationWhenBlank={true}
              disabled={!!lead?.phone}
              name="lead[phone]"
              placeholder="Primary Phone Number"
              onChange={(value) => handleFieldChange('phone', value)}
            />
            <RequiredInputHelper isFilled={!!formData.phone} />
            <span className="mar-lft">
              <CopyURL />
            </span>
          </div>

          {lead?.phone?.id && (
            <>
              <div>
                <label>
                  <input
                    name="call-consent-toggle"
                    type="checkbox"
                    checked={formData.callConsent}
                    onChange={handleCallConsentToggle}
                    style={{ marginRight: '8px' }}
                  />
                  Customer consents to receive sales calls from us
                </label>
                {!formData.callConsent && (
                  <div className="text-danger">
                    Customer has not consented – please confirm that it's ok to get future calls from us!
                  </div>
                )}
              </div>

              <div>
                <label>
                  <input
                    name="sms-consent-toggle"
                    type="checkbox"
                    checked={formData.smsConsent}
                    onChange={handleSMSConsentToggle}
                    style={{ marginRight: '8px' }}
                  />
                  Customer has opted-in to receiving sales messages by SMS
                </label>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-xs-6">
          <HowDidYouHearAboutUs onSelect={handleSourceSelect} source={formData.reportedSource} />
        </div>
      </div>

      <hr />

      <div className="form-inline">
        <div className="pad-btm align-items-center space-between-flex-container">
          <div>
            <label>Check Service Area</label>
            <input
              name="lead[zip]"
              type="text"
              placeholder="Zip Code"
              className="form-control"
              value={formData.zip}
              onChange={(e) => handleZipChange(e.target.value)}
              maxLength={5}
            />
            <div style={{ display: 'inline-block' }}>
              <RequiredInputHelper isFilled={!!formData.zip} />
            </div>
            <div className="checkbox mar-lft">
              <label htmlFor="is-business">
                <input
                  id="is-business"
                  type="checkbox"
                  checked={formData.isBusiness}
                  onChange={(e) => handleFieldChange('isBusiness', e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                Business Account
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-6">
          <ZipCodeStatus
            serviceAreaClassifier={serviceAreaClassifier}
            serviceType={ServiceType.MOVING}
            isChannelPartnerLead={isChannelPartnerLead(lead)}
          />
          <ZipCodeStatus
            serviceAreaClassifier={serviceAreaClassifier}
            serviceType={ServiceType.FULL_SERVICE}
            isChannelPartnerLead={isChannelPartnerLead(lead)}
          />
          <ZipCodeStatus
            serviceAreaClassifier={serviceAreaClassifier}
            serviceType={ServiceType.SELF_STORAGE}
            isChannelPartnerLead={isChannelPartnerLead(lead)}
          />
          <div className="pad-btm">
            <h4>Region Map</h4>
            <AdminMap center={coords || undefined} marker={coords || undefined} pins={pins} />
          </div>
        </div>

        <div className="col-xs-6">
          <h4>Notes</h4>
          <NotesWidget type={NoteNotableEnum.Lead} id={lead.id} />

          <h4>Discounts</h4>
          <DiscountSelector onDiscountSelected={handleDiscountSelected} />

          {coupon && discount && (
            <div className="row mar-top">
              <div className="col-xs-12">
                <p>Discount Name: {discount.name}</p>
                <p>Coupon Name: {coupon.name}</p>
                <p>Applicable Plan Types: {coupon.eligibleServices.join(', ')}</p>
                {coupon.percent > 0 && <p>Percent Off: {(coupon.percent * 100).toFixed(0)}%</p>}
                {coupon.amount > 0 && <p>Amount: ${(-coupon.amount).toFixed(2)}</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Error Display */}
      {saveError && (
        <div style={{ marginTop: '20px' }}>
          <Alert style="danger">{saveError}</Alert>
        </div>
      )}

      {/* Checkout URL Error Display */}
      {checkoutURLError && (
        <div style={{ marginTop: '20px' }}>
          <Alert style="danger">{checkoutURLError}</Alert>
        </div>
      )}

      {/* Action Buttons */}
      <div className="text-right" style={{ marginTop: '20px' }}>
        {lead?.id && (
          <Button
            kind={
              checkoutURLCopyStatus === 'copied' ? 'success' : checkoutURLCopyStatus === 'failed' ? 'danger' : 'info'
            }
            onClick={generateCheckoutURL}
            disabled={checkoutURLLoading}
            style={{ marginRight: '10px' }}
          >
            {checkoutURLLoading
              ? 'Generating...'
              : checkoutURLCopyStatus === 'copied'
                ? 'Copied!'
                : checkoutURLCopyStatus === 'failed'
                  ? 'Copy Failed'
                  : 'Copy Checkout URL'}
          </Button>
        )}
        <Button kind="primary" onClick={saveLead} disabled={updateLoading}>
          {updateLoading ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
};
